import React, {useCallback, useEffect, useMemo} from 'react';
import {useInView} from 'react-intersection-observer';
import {requestContext} from '../../../../common/requestContext';
import {useIsResponsiveTablet} from '../../../../common/useIsResponsiveTablet.hook';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../common/useTypedSelector';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {MainRankingGenreTab} from '../../../../design-system/organisms/MainRankingGenreTab/MainRankingGenreTab';
import {TitleBar} from '../../../../design-system/organisms/TitleBar/TitleBar';
import {TitleBarType} from '../../../../design-system/organisms/TitleBar/TitleBarType.enum';
import {
  MainRankingSection,
  Maybe,
  SectionMainTab,
  SortOption,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {
  getRecentSelectedRankingCategoryTabId,
  saveRecentSelectedRankingCategoryTabId,
} from '../../../../main/rankingCategoryTab.storage';
import {selectSectionMainTab} from '../../../../pagewebLayout/PagewebLayoutSlice';
import {CommonSectionProps} from '../../type';

export function MainRanking({section, children}: CommonSectionProps) {
  const mainRankingSection = section as Omit<MainRankingSection, 'groups'>;
  const {title, sectionMainTabList} = mainRankingSection;

  const dispatch = useTypedDispatch();
  const isMobile = requestContext.requestInfo.display.isMobile;
  const isTablet = useIsResponsiveTablet();
  const goToScheme = useMemo(() => {
    if (isTablet) {
      return mainRankingSection.scheme;
    }
    const found = sectionMainTabList?.find(mainTab => mainTab.isSelected);
    return found?.scheme;
  }, [isTablet, mainRankingSection.scheme, sectionMainTabList]);

  const groups = useTypedSelector(
    state => state.pagewebLayout.relations.sections[section.id].childrenIds
  );
  const currentIndex = useMemo(() => {
    const foundGroupId = sectionMainTabList?.find(mainTab => mainTab.isSelected)
      ?.subTabList[0].groupId;
    if (!foundGroupId) {
      return 0;
    }
    return groups.findIndex(id => id === foundGroupId);
  }, [sectionMainTabList, groups]);

  const rankingGenreTabData: {
    items: SortOption[];
    currentTabItemId: string;
    currentTabItem?: SectionMainTab;
    dateStr?: Maybe<string>;
  } = useMemo(() => {
    const currentTabItem = sectionMainTabList?.find(
      genreTab => genreTab.isSelected
    );

    return {
      items:
        sectionMainTabList?.map(({uid, title}) => ({
          id: String(uid),
          param: String(uid),
          name: title || '',
        })) || [],
      currentTabItemId: String(
        currentTabItem !== undefined
          ? currentTabItem.uid
          : sectionMainTabList?.[0].uid
      ),
      currentTabItem,
      dateStr: currentTabItem?.additionalString,
    };
  }, [sectionMainTabList]);

  const {ref, inView, entry} = useInView();
  useEffect(() => {
    if (inView && entry?.target) {
      aggregateAndSendLog({
        targetElem: entry.target as HTMLElement,
        actionType: 'ViewImp',
        actionName: '섹션_노출',
        customProps: {
          user_action: 'imp',
        },
        viewimp_contents: {
          imp_provider: 'service',
          imp_type: 'vimp',
        },
      });
    }
  }, [entry, inView, rankingGenreTabData.currentTabItem?.title]);

  const handleCategoryTabClick = useCallback(
    (categoryTabUid: string) => {
      const targetRankingTab = rankingGenreTabData.items.find(
        tab => tab.id === categoryTabUid
      );
      aggregateAndSendLog({
        targetElem: entry?.target as HTMLElement,
        actionName: '카테고리_클릭',
        actionType: 'Event',
        customProps: {
          user_action: 'click',
        },
        click: {
          copy: targetRankingTab?.name,
        },
      });
      saveRecentSelectedRankingCategoryTabId(categoryTabUid);
      dispatch(
        selectSectionMainTab({
          sectionId: section.id,
          categoryTabUid: Number(categoryTabUid),
        })
      );
    },
    [dispatch, entry?.target, rankingGenreTabData.items, section.id]
  );

  useEffect(() => {
    if (isMobile) {
      const recentSelectedTabId = getRecentSelectedRankingCategoryTabId();
      if (recentSelectedTabId) {
        dispatch(
          selectSectionMainTab({
            sectionId: section.id,
            categoryTabUid: Number(recentSelectedTabId),
          })
        );
      }
    }
  }, [dispatch, isMobile, section.id]);

  return (
    <div
      ref={ref}
      {...putTiaraObjectProps({
        viewimp_contents: {
          imp_id: 'sranking',
          imp_provider: 'service',
          layer1: '메인랭킹',
        },
        click: {
          imp_id: 'sranking',
          layer1: '메인랭킹',
          imp_provider: 'service',
        },
        customProps: {
          section_title: title,
          page_category_filter: rankingGenreTabData.currentTabItem?.title,
          section_type: '메인랭킹',
        },
      })}
    >
      <TitleBar
        className="mb-4pxr"
        type={TitleBarType.Main}
        title={title || undefined}
        scheme={goToScheme}
        onClickMore={e => LOG_CLICK_EVENT('더보기_클릭', e)}
        {...putTiaraObjectProps({
          eventMeta: {
            id: goToScheme,
            type: 'scheme',
            provider: '서비스',
          },
          customProps: {
            landing_path: 'ranking',
          },
        })}
      >
        {!isTablet && (
          <MainRankingGenreTab
            isMobile={isMobile}
            rankingGenreItemList={rankingGenreTabData.items}
            selectedItemParam={rankingGenreTabData.currentTabItemId}
            dateStr={rankingGenreTabData.dateStr || undefined}
            onClickTabItem={handleCategoryTabClick}
            className="mb-8pxr pr-15pxr"
          />
        )}
      </TitleBar>
      <div className="flex all-child:flex-1">
        {isTablet
          ? React.Children.toArray(children).map((child, index) => {
              return (
                <div key={index}>
                  <div className="flex h-48pxr flex-1 items-center justify-between bg-theme-bg-base px-15pxr">
                    <Text className="font-medium2 text-theme-solid-100 line-clamp-1">
                      {rankingGenreTabData.items[index].name}
                    </Text>
                  </div>
                  {child}
                </div>
              );
            })
          : React.Children.toArray(children)[currentIndex]}
      </div>
    </div>
  );
}
